import { queryOptions, useQuery } from '@tanstack/react-query';
import { isNil } from '@teamsparta/utils';

import {
  getAllRoundsByCourseName,
  getEnrollableRound,
  getPayableRound,
} from '@/api/common/round';
import { getCurrentWeekByRoundId } from '@/api/plus/week';
import type { Course } from '@/types';

export const roundQueryKey = {
  all: ['round'],
  payableRoundByCourseName: (courseName: string) => [
    ...roundQueryKey.all,
    'payable',
    courseName,
  ],
  allRoundsByCourseName: (courseName: string) => [
    ...roundQueryKey.all,
    'all',
    courseName,
  ],
  enrollableRoundByCourseName: (courseName: string) => [
    ...roundQueryKey.all,
    'enrollable',
    courseName,
  ],
  currentWeekByRoundId: (roundId: string) => [
    ...roundQueryKey.all,
    'currentWeek',
    roundId,
  ],
};

export const enrollableRoundQuery = (courseName: string) =>
  queryOptions({
    queryKey: roundQueryKey.enrollableRoundByCourseName(courseName),
    queryFn: () => getEnrollableRound(courseName),
  });

export function useGetAllRoundsByCourseName({
  courseName,
  enabled = true,
}: {
  courseName: Course;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: roundQueryKey.allRoundsByCourseName(courseName),
    queryFn: () => getAllRoundsByCourseName(courseName),
    enabled: !!courseName && enabled,
  });
}

export const payableRoundQuery = (courseName: Course) =>
  queryOptions({
    queryKey: roundQueryKey.payableRoundByCourseName(courseName),
    queryFn: () => getPayableRound(courseName),
  });

export const currentWeekQuery = (roundId: string) =>
  queryOptions({
    queryKey: roundQueryKey.currentWeekByRoundId(roundId),
    queryFn: () => getCurrentWeekByRoundId(roundId),
    select: (data) => {
      if (isNil(data)) {
        return {
          currentWeek: 0,
          startDate: null,
          endDate: null,
        };
      }

      return data;
    },
  });
